/**
 * The app's version where a member can read it out (src/lib/appVersion.ts):
 * Account -> About this app, a small line in the user menu and on the sign-in page,
 * and the "install the latest app" screen when the app is too old for the newest
 * screens (its build is below the live update's min_build).
 */
import { Copy, Info, Smartphone } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { brand } from "@/brand";
import { Badge, Button, Card, CardBody, CardHeader } from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  copyText,
  PLATFORM_LABEL,
  shortLine,
  shortStamp,
  useAppVersion,
  versionLine,
} from "@/lib/appVersion";

/** A small grey line: "v1.0.4 · 02 Nov 18:20" (the full line on hover). */
export function VersionFootnote({ className }: { className?: string }) {
  const v = useAppVersion();
  return (
    <span className={cn("num text-2xs text-subtle", className)} title={versionLine(v)}>
      {shortLine(v)}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid min-w-0 gap-0.5 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className="min-w-0 text-sm break-words text-fg">{children}</dd>
    </div>
  );
}

/** Account -> About this app: what to tell us when something looks wrong. */
export function AboutAppCard() {
  const v = useAppVersion();
  const [full, setFull] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(copyText(v));
      toast.success("Version copied");
    } catch {
      toast(copyText(v));
    }
  }
  const app = v.platform !== "web";
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Info className="size-4 text-brand" aria-hidden />
            This device
          </span>
        }
        action={
          <Button variant="secondary" size="sm" onClick={() => void copy()}>
            <Copy className="size-4" aria-hidden /> Copy version
          </Button>
        }
      />
      <CardBody>
        <dl className="grid gap-3">
          <Row label="Platform">{PLATFORM_LABEL[v.platform]}</Row>
          <Row label="Version">
            <span className="num font-semibold">{versionLine(v)}</span>
          </Row>
          {app ? (
            <Row label="App">
              <span className="num">
                {v.appVersion ?? "?"} (build {v.build ?? "?"})
              </span>
            </Row>
          ) : null}
          <Row label="Screens">
            <button
              type="button"
              className="num text-left underline decoration-dotted underline-offset-4 hover:text-brand"
              onClick={() => setFull((f) => !f)}
              aria-expanded={full}
            >
              {full ? v.screens : (shortStamp(v.screens) ?? v.screens)}
            </button>
          </Row>
          {v.pending ? (
            <Row label="Update">
              <Badge tone="up">Update ready on next restart</Badge>
              <span className="num ml-2 text-xs text-muted">{shortStamp(v.pending) ?? v.pending}</span>
            </Row>
          ) : null}
          {v.needBuild ? (
            <Row label="Update">
              <Badge tone="warn">Install the latest app (build {v.needBuild})</Badge>
            </Row>
          ) : null}
        </dl>
      </CardBody>
    </Card>
  );
}

let dismissed = false; // until the app starts again

/** The app is older than the newest screens need: say so plainly, once per start. */
export function UpdateRequired() {
  const v = useAppVersion();
  const [hidden, setHidden] = useState(dismissed);
  if (!v.needBuild || hidden || v.platform === "web") return null;
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="update-required-h"
      className="fixed inset-0 z-[100] grid place-items-center bg-bg/95 p-4"
    >
      <Card className="w-full max-w-sm">
        <CardBody className="grid gap-4 pt-6 text-center">
          <Smartphone className="mx-auto size-10 text-brand" aria-hidden />
          <h2 id="update-required-h" className="font-display text-lg font-bold text-fg">
            Please install the latest app (build {v.needBuild})
          </h2>
          <p className="text-sm text-muted">
            This app is build {v.build ?? "?"}. The newest {brand.name} screens need build {v.needBuild}: install
            the latest version from the store or the link we sent you, then open it again.
          </p>
          <p className="num text-xs text-subtle">{versionLine(v)}</p>
          <div className="grid gap-2">
            <a className="text-sm font-medium text-brand" href={`mailto:${brand.supportEmail}`}>
              Need help? {brand.supportEmail}
            </a>
            <Button
              variant="secondary"
              onClick={() => {
                dismissed = true;
                setHidden(true);
              }}
            >
              Not now
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
